import { FDRStatus, UserId } from '@cibo/core'
import { useAllProgramContent, useProgramEngagements, useProgramStats } from '@cibo/programs'
import { assoc, equals, indexBy, pipe, prop, sort } from 'ramda'
import { UserProgramCardData } from './UserProgramCardTypes'
import { getUserProgramStatus } from './getUserProgramStatus'

export const useUserProgramStatusCards = ({ userId }: { userId: UserId }) => {
  const programStats = useProgramStats({
    userId,
    fdrStats: [
      { status: FDRStatus.eligibleForEnrollment },
      { status: FDRStatus.ineligibleForEnrollment },
      { status: FDRStatus.enrolling },
      { status: FDRStatus.enrolled },
    ],
  })

  const programContent = useAllProgramContent()

  const engagements = useProgramEngagements({ filters: [{ userId: [userId] }] })

  const programs: Partial<UserProgramCardData>[] | undefined = programStats.data?.programs.map(
    ({ programId, fdrStats }) => ({
      programId,
      ...fdrStats?.reduce(
        (acc, { status, acres }) => ({
          ...acc,
          [status]: acres,
        }),
        {}
      ),
    })
  )

  const programsById =
    programs && indexBy(prop('programId'), programs as Array<{ programId: string }>)

  if (programsById) {
    engagements.data?.items.forEach(({ programId, currentStepType }) => {
      if (programsById && programsById[programId]) {
        // @ts-ignore allow adding the property
        programsById[programId].currentStepType = currentStepType
      }
    })
  }

  const programsWithStatuses = programs?.map(userProgramData =>
    pipe(
      assoc('cardStatus', getUserProgramStatus(userProgramData)),
      assoc(
        'title',
        programContent.data?.find(a => a.programId === userProgramData.programId)?.content.fields
          .title
      )
    )(userProgramData)
  )

  let alphaProgramsWithStatuses

  if (
    !!programsWithStatuses &&
    equals(
      programContent.data &&
        sort(
          (a, b) => a?.localeCompare(b),
          programContent.data?.map(a => a.programId)
        ),
      sort(
        (a, b) => (a && b && a.localeCompare(b)) || 1,
        programsWithStatuses?.map(a => a.programId)
      )
    )
  ) {
    alphaProgramsWithStatuses = sort(
      // Above if checks that a.title and b.title are available in matching pairs
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (a, b) => a.title!.localeCompare(b.title!),
      programsWithStatuses
    ) as UserProgramCardData[]
  }

  return {
    data: alphaProgramsWithStatuses || (programsWithStatuses as UserProgramCardData[]),
    isLoading: programContent.isLoading || programStats.isLoading || engagements.isLoading,
    isError: programContent.isError || programStats.isError || engagements.isError,
  }
}
